import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { useReactTable, createColumnHelper, getCoreRowModel, flexRender, getFilteredRowModel } from "@tanstack/react-table";
import { FaEraser } from "react-icons/fa";

import Spinner from '../components/Spinner';
import Pagination from '../components/Pagination';
import SuccessModal from '../components/modals/SuccessModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { useNotificationModal } from '../hooks/useNotificationModal';
import { AuthContext } from '../context/AuthContext';
import '../styles/ProductOffers.css';
import { Button } from 'react-bootstrap';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const columnHelper = createColumnHelper();

export default function ProductOffers() {
  const { userData } = useContext(AuthContext);

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const debounceTimeout = useRef();
  const [sortOption, setSortOption] = useState("all");
  // Inline offer fields (apply to selected rows)
  const [offerPrice, setOfferPrice] = useState("");
  const [offerStartDate, setOfferStartDate] = useState("");
  const [offerEndDate, setOfferEndDate] = useState("");
  const [applyLoading, setApplyLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [columnFilters, setColumnFilters] = useState({});
  // Confirm modal state for bulk remove
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmRemoveData, setConfirmRemoveData] = useState(null);

  // Clear all filters
  const clearFilters = () => {
    setColumnFilters({});
    setGlobalFilter("");
    setDebouncedFilter("");
    // refresh list to default view
    fetchProducts(1, "", sortOption || "all");
  };
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Helper to derive a stable id for a row
  const getRowUniqueId = (item) => {
    if (!item) return "";
    const style = item.style_number || item.styleNumber || "";
    const sku = item.sku || "";
    const location =
      (item.location && (item.location.name || item.location.location_name)) ||
      item.location_name ||
      item.location_id ||
      "";
    const batch = item.batch_number || item.batchNumber || "";
    const lot_no = item.lot_no || item.lotNo || "";

    const composite = [style, sku, location, batch, lot_no]
      .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
      .join("|");
    if (composite.replace(/\|+/g, "") !== "") return composite;

    // fall back to any available id
    return item.id || item._id || "";
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else if (id) s.add(id);
      return s;
    });
  };

  const selectAllOnPage = useCallback((rows, checked) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      rows.forEach((r) => {
        const id = getRowUniqueId(r.original);
        if (!id) return;
        if (checked) s.add(id);
        else s.delete(id);
      });
      return s;
    });
  }, []);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Helper to normalize date
    const toISODate = (raw) => {
      if (!raw && raw !== 0) return "";
      try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return "";
        return d.toISOString().split("T")[0];
      } catch (e) {
        return "";
      }
    };

    return data.filter((item) => {
      return Object.entries(columnFilters).every(([colId, filterValue]) => {
        if (!filterValue || String(filterValue).trim() === "") return true;

        // For date columns, compare normalized ISO dates for exact match
        const dateCols = ["created_at", "offer_start_date", "offer_end_date"];
        if (dateCols.includes(colId)) {
          const itemDateISO = toISODate(item[colId]);
          return itemDateISO === String(filterValue);
        }

        const raw = item[colId];
        // normalize values to strings for simple contains matching
        const str = raw === undefined || raw === null ? "" : String(raw);
        return str.toLowerCase().includes(String(filterValue).toLowerCase());
      });
    });
  }, [data, columnFilters]);

  // Define table columns
  const columns = useMemo(
    () => [
      // Selection column
      columnHelper.display({
        id: "select",
        header: ({ table }) => {
          const rows = table.getRowModel().rows;
          const allSelected =
            rows.length > 0 &&
            rows.every((r) => selectedIds.has(getRowUniqueId(r.original)));
          return (
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => selectAllOnPage(rows, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              title="Select all on page"
            />
          );
        },
        cell: ({ row }) => {
          const id = getRowUniqueId(row.original);
          return (
            <input
              type="checkbox"
              checked={selectedIds.has(id)}
              onChange={() => toggleSelect(id)}
              onClick={(e) => e.stopPropagation()}
              title="Select row"
            />
          );
        },
      }),
      columnHelper.accessor("style_number", {
        header: "Product Number",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("style_name", {
        header: "Product Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("sku", {
        header: "SKU",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("batch_number", {
        header: "Batch Number",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("lot_no", {
        header: "Lot Number",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor(
        (row) => row.location?.name || row.location_name || "",
        {
          id: "location_name",
          header: "Location",
          cell: (info) => info.getValue() || "-",
        }
      ),
      columnHelper.accessor("unit_price", {
        header: "Unit Price",
        cell: (info) => `$${info.getValue() || 0}`,
        meta: { className: "text-end" },
      }),
      columnHelper.accessor("sale_price", {
        header: "Sale Price",
        cell: (info) => `$${info.getValue() || 0}`,
        meta: { className: "text-end" },
      }),
      columnHelper.accessor("main_stock_qty", {
        header: "Stock Quantity",
        cell: (info) => info.getValue(),
        meta: { className: "text-end" },
      }),
      columnHelper.accessor("created_at", {
        header: "Stock Received Date",
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      }),
      columnHelper.accessor("offer_price", {
        header: "Offer Price",
        meta: { className: "text-end" },
        cell: (info) => {
          const val = info.getValue();
          return val && Number(val) !== 0 ? `$${val}` : "-";
        },
      }),
      columnHelper.accessor("offer_start_date", {
        header: "Offer Start Date",
        cell: (info) => {
          const val = info.getValue();
          return val ? new Date(val).toLocaleDateString() : "-";
        },
      }),
      columnHelper.accessor("offer_end_date", {
        header: "Offer End Date",
        cell: (info) => {
          const val = info.getValue();
          if (!val) return "-";
          const endDate = new Date(val);
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          const isExpired = endDate < currentDate;

          return (
            <span className={isExpired ? "pf-expired-date" : ""}>
              {endDate.toLocaleDateString()}
            </span>
          );
        },
      }),
    ],
    [selectedIds, selectAllOnPage]
  );

  // Fetch product data
  const fetchProducts = useCallback(
    async (page = 1, search = "", filterType = "all") => {
      try {
        setLoading(true);
        let url = `${BASE_URL}/api/admin/offers/product-details`;
        let params = {
          company_code: userData?.company_code,
          page: page,
          limit: 10,
        };
        // search
        if (search && search.trim() !== "") {
          url = `${BASE_URL}/api/admin/offers/search`;
          params.searchInput = search;
          // filter
        } else if (filterType && filterType !== "all") {
          url = `${BASE_URL}/api/admin/offers/filter`;
          params.filter = filterType;
        }
        const response = await axios.get(url, { params });
        setData(response.data.products || response.data || []);
        setPagination(response.data.pagination || {});
      } catch (error) {
        console.error("Error fetching products:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [userData?.company_code]
  );

  // Debounce globalFilter changes
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedFilter(globalFilter);
    }, 600);
    return () => clearTimeout(debounceTimeout.current);
  }, [globalFilter]);

  // Fetch products when debouncedFilter or sortOption changes
  useEffect(() => {
    if (debouncedFilter && debouncedFilter.trim() !== "") {
      fetchProducts(1, debouncedFilter, "all");
    } else {
      fetchProducts(1, "", sortOption);
    }
  }, [debouncedFilter, sortOption, fetchProducts]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      if (debouncedFilter && debouncedFilter.trim() !== "") {
        fetchProducts(newPage, debouncedFilter, "all");
      } else {
        fetchProducts(newPage, "", sortOption);
      }
    }
  };

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  // Apply offer to selected rows inline
  const handleApplyOffers = async () => {
    if (!selectedIds || selectedIds.size === 0) {
      showError(
        "Please select one or more products to apply the offer.",
        "Error"
      );
      return;
    }

    // basic validation
    if (!offerPrice || Number(offerPrice) <= 0) {
      showError("Please enter a valid offer price.", "Error");
      return;
    }

    const targets = data.filter((item) =>
      selectedIds.has(getRowUniqueId(item))
    );
    if (!targets || targets.length === 0) {
      showError("No valid products selected.", "Error");
      return;
    }
    // Validate offer price against unit_price and sale_price of all selected items
    const offer = parseFloat(offerPrice);
    const failures = [];
    targets.forEach((t) => {
      const sku = t.sku || t.style_number || "item";
      const unit = parseFloat(t.unit_price || 0);
      const sale = parseFloat(t.sale_price || 0);
      if (offer < unit) {
        failures.push(`${sku} (unit: $${unit})`);
      } else if (sale && offer >= sale) {
        failures.push(`${sku} (sale: $${sale})`);
      }
    });

    if (failures.length > 0) {
      showError(
        `Offer price invalid for: ${failures.join(
          ", "
        )}. Please adjust the offer.`,
        "Error"
      );
      return;
    }

    // Validate dates if provided
    if (offerStartDate && offerEndDate) {
      const s = new Date(offerStartDate);
      const e = new Date(offerEndDate);
      if (!(e > s)) {
        showError("End date must be after start date.", "Error");
        return;
      }
    }

    setApplyLoading(true);

    try {
      const common = {
        company_code: userData?.company_code,
        offer_price: parseFloat(offerPrice),
        offer_start_date: offerStartDate || null,
        offer_end_date: offerEndDate || null,
      };

      const promises = targets.map((t) => {
        const body = { ...common, product_id: t.id, sku: t.sku };
        return axios
          .post(`${BASE_URL}/api/admin/offers/create-offer`, body)
          .then((res) => ({
            success: res.data?.success,
            sku: t.sku,
            data: res.data,
          }))
          .catch((err) => ({ success: false, sku: t.sku, error: err }));
      });

      const results = await Promise.all(promises);
      const failed = results.filter((r) => !r.success);
      if (failed.length === 0) {
        showSuccess(`Offer saved for ${results.length} product(s)!`, "Success");
        // refresh
        fetchProducts(
          pagination.currentPage || 1,
          debouncedFilter || "",
          sortOption || "all",
          setSelectedIds(new Set())
        );
        // clear inputs
        setOfferPrice("");
        setOfferStartDate("");
        setOfferEndDate("");
      } else {
        console.error("Some offers failed:", failed);
        showError(
          `Failed to save offers for ${failed.length} product(s).`,
          "Error"
        );
      }
    } catch (err) {
      console.error("Error applying offers:", err);
      showError(
        err.response?.data?.message || "Failed to apply offers.",
        "Error"
      );
    } finally {
      setApplyLoading(false);
    }
  };

  // Bulk remove offers: clear offer fields for selected SKUs
  const handleBulkRemoveOffers = async () => {
    if (!selectedIds || selectedIds.size === 0) {
      showError(
        "Please select one or more products to remove offers from.",
        "Error"
      );
      return;
    }

    const skus = Array.from(selectedIds)
      .map((id) => {
        const found = data.find((d) => getRowUniqueId(d) === id);
        return found ? found.sku : null;
      })
      .filter(Boolean);

    if (skus.length === 0) {
      showError("No valid SKUs found for selected products.", "Error");
      return;
    }

    // Show confirmation modal
    setConfirmRemoveData(skus);
    setShowConfirmModal(true);
  };

  // Handle confirmed removal
  const handleConfirmRemove = async () => {
    if (!confirmRemoveData || confirmRemoveData.length === 0) return;

    setApplyLoading(true);
    try {
      const body = { company_code: userData?.company_code, skus: confirmRemoveData };
      const resp = await axios.put(
        `${BASE_URL}/api/admin/offers/bulk-remove`,
        body
      );
      if (resp.data && resp.data.success) {
        showSuccess(
          `Removed offers for ${
            resp.data.affectedRows || confirmRemoveData.length
          } product(s).`,
          "Success"
        );
        fetchProducts(
          pagination.currentPage || 1,
          debouncedFilter || "",
          sortOption || "all"
        );
        setShowConfirmModal(false);
        setConfirmRemoveData(null);
      } else {
        showError(resp.data?.message || "Failed to remove offers.", "Error");
      }
    } catch (err) {
      console.error("Bulk remove error:", err);
      showError(
        err.response?.data?.error || "Failed to remove offers.",
        "Error"
      );
    } finally {
      setApplyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pf-product-offers-container">
        <div className="style-header">
          <h1>Product Offers</h1>
        </div>
        <div className="pf-loading-container">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="pf-product-offers-container">
      <div className="product-offers-header">
        <h1>Product Offers</h1>
      </div>

      <div className="">
        <div className="pf-controls-container justify-space-between">
          <div className="pf-offers-container">
            <p style={{ fontWeight: "500", color: "#374151" }}>
              {" "}
              Select products and enter offer details{" "}
              {selectedIds.size > 0 && (
                <span style={{ color: "#059669", fontWeight: "600" }}>
                  ({selectedIds.size} SKU{selectedIds.size !== 1 ? 's' : ''} selected)
                </span>
              )}
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginTop: 6,
              }}
            >
              <input
                type="number"
                placeholder="Offer Price"
                value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)}
                step="0.01"
                min="0"
                style={{ padding: "6px 8px", width: 130 }}
              />
              <input
                type="date"
                placeholder="Start Date"
                value={offerStartDate}
                onChange={(e) => setOfferStartDate(e.target.value)}
                style={{ padding: "6px 8px" }}
              />
              <input
                type="date"
                placeholder="End Date"
                value={offerEndDate}
                onChange={(e) => setOfferEndDate(e.target.value)}
                style={{ padding: "6px 8px" }}
              />
              <Button
                variant="success"
                className="pf-add-offers-button"
                onClick={handleApplyOffers}
                disabled={selectedIds.size === 0 || applyLoading}
              >
                {applyLoading ? "Applying..." : "Apply to selected"}
              </Button>
              <Button
                className="pf-remove-offers-button"
                onClick={handleBulkRemoveOffers}
                disabled={selectedIds.size === 0 || applyLoading}
                variant="danger"
              >
                {applyLoading ? "Removing..." : "Remove selected"}
              </Button>
            </div>
          </div>
          <div className="pf-sort-container">
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="pf-sort-select"
            >
              <option value="all">All Products</option>
              <option value="active_offers">Active Offers Only</option>
              <option value="upcoming_offers">Upcoming Offers Only</option>
              <option value="expired_offers">Expired Offers Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="pf-table-container">
        <div className="pf-table-wrapper">
          <table className="pf-products-table">
            <thead className="pf-table-header">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="pf-table-body">
              {/* Filter row: first row of tbody with inputs per column */}
              <tr className="pf-table-filter-row">
                {table.getHeaderGroups()[0].headers.map((header, idx) => {
                  // determine column id from header
                  const colId = header.column?.id || header.id || String(idx);

                  // For the selection column, a clear-filters button in the filter row
                  if (colId === "select") {
                    return (
                      <td
                        key={`filter-${colId}`}
                        className="pf-table-cell pf-filter-cell"
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Button
                            variant="outline"
                            className="pf-clear-filters-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearFilters();
                            }}
                            title="Clear filters"
                          >
                            <FaEraser />
                          </Button>
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={`filter-${colId}`}
                      className="pf-table-cell pf-filter-cell"
                    >
                      {[
                        "created_at",
                        "offer_start_date",
                        "offer_end_date",
                      ].includes(colId) ? (
                        <input
                          type="date"
                          value={columnFilters[colId] || ""}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({
                              ...prev,
                              [colId]: e.target.value,
                            }))
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="pf-column-filter-input"
                        />
                      ) : (
                        <input
                          type="text"
                          value={columnFilters[colId] || ""}
                          onChange={(e) =>
                            setColumnFilters((prev) => ({
                              ...prev,
                              [colId]: e.target.value,
                            }))
                          }
                          onClick={(e) => e.stopPropagation()} // prevent triggering row click
                          placeholder={
                            header.column?.columnDef?.header
                              ? String(header.column.columnDef.header)
                              : ""
                          }
                          className="pf-column-filter-input"
                        />
                      )}
                    </td>
                  );
                })}
              </tr>

              {table.getRowModel().rows.map((row) => {
                const rowId = getRowUniqueId(row.original);
                const isSelected = rowId && selectedIds.has(rowId);
                return (
                  <tr
                    key={row.id}
                    className={`pf-table-row ${
                      isSelected ? "pf-row-selected" : ""
                    }`}
                    onClick={(e) => {
                      const tag =
                        e.target && e.target.tagName
                          ? e.target.tagName.toUpperCase()
                          : "";
                      // ignore clicks on interactive elements so they behave normally
                      if (
                        [
                          "INPUT",
                          "BUTTON",
                          "A",
                          "SELECT",
                          "LABEL",
                          "TEXTAREA",
                        ].includes(tag)
                      )
                        return;
                      if (rowId) toggleSelect(rowId);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      // Columns to align right
                      const rightAlignColumns = [
                        "unit_price",
                        "sale_price",
                        "offer_price",
                        "main_stock_qty",
                      ];
                      const isRightAligned = rightAlignColumns.includes(
                        cell.column.id
                      );
                      return (
                        <td
                          key={cell.id}
                          className={`pf-table-cell ${
                            index === 0
                              ? "pf-index-cell"
                              : cell.column.id === "style_number"
                              ? "pf-style-number-cell"
                              : cell.column.id === "unit_price" ||
                                cell.column.id === "sale_price"
                              ? "pf-price-cell"
                              : cell.column.id === "offer_price"
                              ? "pf-offer-price-cell"
                              : cell.column.id === "main_stock_qty"
                              ? "pf-stock-cell"
                              : cell.column.id === "created_at" ||
                                cell.column.id === "offer_start_date" ||
                                cell.column.id === "offer_end_date"
                              ? "pf-date-cell"
                              : ""
                          }${isRightAligned ? " pf-align-right" : ""}`}
                          style={isRightAligned ? { textAlign: "right" } : {}}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* No data message */}
      {filteredData.length === 0 && !loading && (
        <div className="pf-no-data-message">
          {sortOption === "active_offers"
            ? "No active offers found."
            : sortOption === "upcoming_offers"
            ? "No upcoming offers found."
            : sortOption === "expired_offers"
            ? "No expired offers found."
            : "No products found."}
        </div>
      )}

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={handlePageChange} />

      {/* Confirm Remove Offers Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setConfirmRemoveData(null);
        }}
        onConfirm={handleConfirmRemove}
        title="Remove Offers"
        message={`Are you sure you want to remove offers from ${
          confirmRemoveData?.length || 0
        } product(s)? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={applyLoading}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </div>
  );
}